import React, { useState, useEffect } from 'react';
import {
  BookOpen, Play, GitBranch, Clock, Hash,
  Loader2, Search, ChevronDown, ChevronUp, Code2
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import CodeReviewPanel from './CodeReviewPanel';

interface LibraryEntry {
  id: string;
  engagement_id: string | null;
  label: string;
  description: string;
  fn_name: string;
  code: string;
  params_schema_json: Record<string, any>;
  tags: string[];
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
}

interface TransformationLibraryProps {
  engagementId: string;
  sessionId: number | null;
  dfPath: string;
  rowCount: number;
  onTransformApplied?: (result: any) => void;
}

const TransformationLibrary: React.FC<TransformationLibraryProps> = ({
  engagementId, sessionId, dfPath, rowCount, onTransformApplied,
}) => {
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewEntry, setReviewEntry] = useState<LibraryEntry | null>(null);

  useEffect(() => {
    loadLibrary();
  }, [engagementId]);

  const loadLibrary = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/custom-transform/library', {
        params: { engagement_id: engagementId },
      });
      if (response.data.success) {
        setEntries(response.data.data || []);
      }
    } catch {
      toast.error('Failed to load transformation library');
    } finally {
      setLoading(false);
    }
  };

  const filtered = entries.filter(e => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      e.label.toLowerCase().includes(q) ||
      e.description?.toLowerCase().includes(q) ||
      e.fn_name.toLowerCase().includes(q)
    );
  });

  const handleApply = (entry: LibraryEntry) => {
    setReviewEntry(entry);
  };

  const handleReviewExecuted = (result: any) => {
    setReviewEntry(null);
    loadLibrary();
    if (onTransformApplied) {
      onTransformApplied(result);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        <span className="ml-2 text-sm text-gray-500">Loading library...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-violet-600" />
          <h3 className="text-sm font-bold text-gray-800">Transformation Library</h3>
          <span className="px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
            {entries.length}
          </span>
        </div>
      </div>

      {entries.length > 3 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search transformations..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Code2 className="w-10 h-10 text-violet-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {entries.length === 0 ? 'No saved transformations yet' : 'No results match your search'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Use the Custom Transformation Studio to create and save reusable transforms.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(entry => {
            const isExpanded = expandedId === entry.id;
            const isGlobal = !entry.engagement_id;

            return (
              <div key={entry.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-800 truncate">{entry.label}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                        isGlobal ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {isGlobal ? 'Global' : 'Engagement'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.description || entry.fn_name}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Hash className="w-3 h-3" />
                      {entry.usage_count}
                    </div>
                    {entry.last_used_at && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {new Date(entry.last_used_at).toLocaleDateString()}
                      </div>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
                    <div className="mt-3">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Function: <span className="font-mono text-violet-600">{entry.fn_name}</span>
                      </h4>
                      <pre className="w-full max-h-48 px-3 py-2 font-mono text-xs bg-gray-900 text-green-300 rounded-lg overflow-auto scrollbar-thin">
                        {entry.code}
                      </pre>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleApply(entry)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
                      >
                        <Play className="w-3.5 h-3.5" />
                        Apply to current dataset
                      </button>
                      <button
                        onClick={() => {
                          toast.success('Code copied — open Custom Transform Studio to edit');
                          navigator.clipboard.writeText(entry.code);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                      >
                        <GitBranch className="w-3.5 h-3.5" />
                        Fork & Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {reviewEntry && (
        <CodeReviewPanel
          transform={{
            fn_name: reviewEntry.fn_name,
            code: reviewEntry.code,
            params_schema: reviewEntry.params_schema_json || {},
            explanation: reviewEntry.description || '',
            estimated_match_rates: {},
            warnings: [],
          }}
          rowCount={rowCount}
          dfPath={dfPath}
          sessionId={sessionId}
          engagementId={engagementId}
          promptSummary={`Library replay: ${reviewEntry.label}`}
          onClose={() => setReviewEntry(null)}
          onExecuted={handleReviewExecuted}
        />
      )}
    </div>
  );
};

export default TransformationLibrary;
